/**
 * Shared encoding and hashing helpers for the P2P social demo.
 *
 * The wire protocol is line-oriented UTF-8 text. To keep framing simple and copy/paste friendly,
 * binary-safe fields (public keys, signatures, text payload) are encoded using base64url (RFC 4648)
 * without padding.
 */
package com.oasisfeng.invaluable.identity

import org.kotlincrypto.hash.sha2.SHA256

object Codec {

    /** UTF-8 encoding for deterministic bytes on the wire and in signatures. */
    fun utf8(str: String): ByteArray = str.encodeToByteArray()
    fun utf8(bytes: ByteArray): String = bytes.decodeToString()

    /** Base64url is used to keep single-line framing simple (no escaping or binary frames). */
    fun base64Url(bytes: ByteArray): String {
        if (bytes.isEmpty()) return ""
        val out = StringBuilder(((bytes.size + 2) / 3) * 4)
        var i = 0
        while (i + 3 <= bytes.size) {
            val b0 = bytes[i++].toInt() and 0xFF
            val b1 = bytes[i++].toInt() and 0xFF
            val b2 = bytes[i++].toInt() and 0xFF
            out.append(BASE64_URL_ALPHABET[b0 ushr 2])
            out.append(BASE64_URL_ALPHABET[((b0 and 0x03) shl 4) or (b1 ushr 4)])
            out.append(BASE64_URL_ALPHABET[((b1 and 0x0F) shl 2) or (b2 ushr 6)])
            out.append(BASE64_URL_ALPHABET[b2 and 0x3F])
        }
        val remaining = bytes.size - i
        if (remaining == 1) {
            val b0 = bytes[i].toInt() and 0xFF
            out.append(BASE64_URL_ALPHABET[b0 ushr 2])
            out.append(BASE64_URL_ALPHABET[(b0 and 0x03) shl 4])
        } else if (remaining == 2) {
            val b0 = bytes[i++].toInt() and 0xFF
            val b1 = bytes[i].toInt() and 0xFF
            out.append(BASE64_URL_ALPHABET[b0 ushr 2])
            out.append(BASE64_URL_ALPHABET[((b0 and 0x03) shl 4) or (b1 ushr 4)])
            out.append(BASE64_URL_ALPHABET[(b1 and 0x0F) shl 2])
        }
        return out.toString()
    }

    fun base64UrlDecode(str: String): ByteArray {
        if (str.isEmpty()) return byteArrayOf()
        val len = str.length
        val mod = len % 4
        require(mod != 1) { "Invalid base64url length: $len" }

        val fullQuads = len / 4
        val outLen = fullQuads * 3 + when (mod) {
            0 -> 0
            2 -> 1
            3 -> 2
            else -> error("Unreachable mod: $mod")
        }
        val out = ByteArray(outLen)

        var inPos = 0
        var outPos = 0
        repeat(fullQuads) {
            val c0 = decodeBase64UrlChar(str[inPos++])
            val c1 = decodeBase64UrlChar(str[inPos++])
            val c2 = decodeBase64UrlChar(str[inPos++])
            val c3 = decodeBase64UrlChar(str[inPos++])

            out[outPos++] = ((c0 shl 2) or (c1 ushr 4)).toByte()
            out[outPos++] = (((c1 and 0x0F) shl 4) or (c2 ushr 2)).toByte()
            out[outPos++] = (((c2 and 0x03) shl 6) or c3).toByte()
        }

        if (mod == 2) {
            val c0 = decodeBase64UrlChar(str[inPos++])
            val c1 = decodeBase64UrlChar(str[inPos])
            out[outPos] = ((c0 shl 2) or (c1 ushr 4)).toByte()
        } else if (mod == 3) {
            val c0 = decodeBase64UrlChar(str[inPos++])
            val c1 = decodeBase64UrlChar(str[inPos++])
            val c2 = decodeBase64UrlChar(str[inPos])
            out[outPos++] = ((c0 shl 2) or (c1 ushr 4)).toByte()
            out[outPos] = (((c1 and 0x0F) shl 4) or (c2 ushr 2)).toByte()
        }
        return out
    }

    /** SHA-256 is used for both event IDs and link IDs. */
    fun sha256(bytes: ByteArray): ByteArray = SHA256().digest(bytes)

    fun sha256Hex(bytes: ByteArray): String = hex(sha256(bytes))

    /** Lower-case hex encoding to keep IDs compact and copyable in terminals. */
    fun hex(bytes: ByteArray): String = buildString(bytes.size * 2) {
        for (b in bytes) {
            val i = b.toInt() and 0xFF
            append(HEX_CHARS[i ushr 4])
            append(HEX_CHARS[i and 0x0F])
        }
    }

    fun hexDecode(str: String): ByteArray {
        require(str.length % 2 == 0) { "Hex string must have even length: ${str.length}" }
        val out = ByteArray(str.length / 2)
        var i = 0
        while (i < str.length) {
            val hi = str[i].digitToInt(16)
            val lo = str[i + 1].digitToInt(16)
            out[i / 2] = ((hi shl 4) or lo).toByte()
            i += 2
        }
        return out
    }

    private fun decodeBase64UrlChar(c: Char): Int {
        val code = c.code
        if (code < 0 || code >= BASE64_URL_DECODE.size) error("Invalid base64url char: $c")
        val v = BASE64_URL_DECODE[code]
        if (v < 0) error("Invalid base64url char: $c")
        return v
    }

    private val HEX_CHARS = "0123456789abcdef".toCharArray()

    private const val BASE64_URL_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_"

    private val BASE64_URL_DECODE: IntArray = IntArray(128) { -1 }.also { table ->
        for (i in BASE64_URL_ALPHABET.indices) {
            table[BASE64_URL_ALPHABET[i].code] = i
        }
    }
}
